'use client';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Mousewheel, Keyboard } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import {
  ArrowUpIcon, ArrowDownIcon, SpeakerWaveIcon, SpeakerXMarkIcon, XMarkIcon,
} from '@heroicons/react/16/solid';
import {
  Button, LinkStyled, Modal,
} from '@components/common';
import {
  useCallback, useRef, useState,
} from 'react';
import {
  PauseIcon, PhoneIcon, PlayIcon,
} from '@heroicons/react/20/solid';
import { CustomConfirmModalContent } from '@components/templates/cv/partials/modals/CustomConfirmModalContent';
import { useAuthClient } from '@root/src/hooks';
import { useSwrResumeCheckUnlock } from '@hooks/useSwrApi/endpoints/resume-operations';
import { showAddCompanyInfoModal } from '@components/features/modals/employer/AddCompanyInfoModal';
import { showUpgradeSubscriptionModal } from '@components/features/modals/employer/subscription/UpgradeSubscriptionModal';
import { trackEvent } from '@services/tracking';
import { useAppSelector } from '@store/index';
import { getUserHeaderInfo } from '@store/reducers/userSelector';
import { StoriesActionIcons } from '@components/templates/stories/StoriesActionIcons';
import { useSwrStoriesFetchSingle } from '@hooks/useSwrApi/endpoints/stories/useSwrStoriesFetchSingle';


interface SwiperStoriesCardsProps {
  locale: Locale;
  slug: string;
  onClose?: () => void;
  openTalentModal?: (userSlug: string, as?: string) => void,
}

export const SwiperStoriesCards = ({
  locale, slug, onClose, openTalentModal,
}: SwiperStoriesCardsProps) => {
  const headerInfoData = useAppSelector(getUserHeaderInfo);
  const userId = headerInfoData?.userId;
  const videoRefs = useRef<HTMLVideoElement[]>([]);
  const [swiperIndex, setSwiperIndex] = useState(0);
  const { isAuth } = useAuthClient();

  const [confirmModal, setConfirmModal] = useState<boolean>(false);
  const [muted, setMuted] = useState(true);
  const [hasInteracted, setHasinteracted] = useState(false);
  const [showIcon, setShowIcon] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const iconTimeout = useRef<NodeJS.Timeout | null>(null);
  const progressRefs = useRef<HTMLDivElement[]>([]);

  const {
    data,
  } = useSwrStoriesFetchSingle({
    locale,
    slug,
  });

  const items: ApiSchema<'InterviewVideoRead2'>[] = data?.flat() ?? [];

  const toggleMuteAll = useCallback((value?: string) => {
    if (!value) return;

    if (value === 'true') {
      setMuted(true);
    } else if (value === 'false') {
      setMuted(false);
    } else if (value === 'prev') {
      setMuted((prev) => !prev);
    }
  }, []);


  const { trigger: triggerUnlock } = useSwrResumeCheckUnlock({
    userSlug: slug ?? '',
    type: 'contact',
    locale,
    successCallback: () => {
      setConfirmModal(false);
      openTalentModal?.(slug);
    },
    errorCallback: (errors) => {
      const code = errors?.errors?.[0]?.code;

      if (!code) {
        console.error('Unlock failed:', errors);
        return;
      }

      if (code === 403) {
        showAddCompanyInfoModal(locale);
      } else if (code === 601) {
        showUpgradeSubscriptionModal();
      } else {
        console.error('Unlock failed:', errors);
      }
    },
  });

  const updateVideos = (activeIndex: number) => {
    videoRefs.current.forEach((video, i) => {
      if (!video) return;
      if (i === activeIndex) {
        void video.play().catch(() => {});
      } else {
        video.pause();
        video.currentTime = 0;
      }
    });
  };


  const handleStoriesStart = (index:number) => {
    const video = videoRefs.current[index];
    if (!video) return;
    video.play().catch(() => {});
    toggleMuteAll('false');
    setHasinteracted(true);
  };

  const handleVideoClick = useCallback(
    (index: number) => {
      if (!hasInteracted) return;

      const video = videoRefs.current[index];
      if (!video) return;

      if (video.paused) {
        video.play().catch(() => {});
        setIsPlaying(true);
        toggleMuteAll('false');
      } else {
        video.pause();
        setIsPlaying(false);
        toggleMuteAll('true');
      }

      // Show icon for 1 second
      setShowIcon(true);
      if (iconTimeout.current) clearTimeout(iconTimeout.current);
      iconTimeout.current = setTimeout(() => setShowIcon(false), 1000);
    },
    [hasInteracted, toggleMuteAll],
  );

  return (
    <div className="relative h-[calc(100dvh-100px-env(safe-area-inset-bottom))] w-full">

      {isAuth && (
        <Modal
          size="sm"
          isOpen={confirmModal}
          body={(
            <CustomConfirmModalContent
              doNotShowMore
              userSlug={slug ?? ''}
              onSendSuccess={(val: boolean) => {
                if (!val) {
                  setConfirmModal(false);
                  return;
                }
                if (val) {
                  void triggerUnlock({ jobId: 0, jobSlug: '', title: '' });
                }
              }}
            />
          )}
          onClose={() => null}
        />
      )}


      {items && items.length > 0 && (
        <>
          <Swiper
            direction="vertical"
            modules={[Navigation, Mousewheel, Keyboard]}
            keyboard={{ enabled: true, onlyInViewport: false }}
            mousewheel
            navigation={{ nextEl: '.next-btn', prevEl: '.prev-btn' }}
            slidesPerView={1}
            observeParents
            observer
            className="h-full"
            onSlideChange={(swiper) => {
              const index = swiper.activeIndex;
              setHasinteracted(true);
              setSwiperIndex(index);

              // setActiveVideoSlug(activeItem.slug ?? '');
              updateVideos(swiper.activeIndex);
              trackEvent({
                eventType: 'view',
                listType: 'stories',
                entityType: 'resume',
                entityId: slug ?? '',
                viewContext: 'detail',
                ...(userId && { userId: userId.toString() }),
                metadata: [
                  { key: 'video', value: slug ?? '' },
                ],
              });
            }}
          >
            {items?.map((item: ApiSchema<'InterviewVideoRead2'>, index :number) => {
              // TODO change URL to stream
              // const streamUrl = item.streams?.find((s) => s.type === 'hls')?.url || item.url;
              const streamUrl = item.url;
              return (
                <SwiperSlide
                  key={item.id}
                  className="relative overflow-hidden border-surface-100"
                >
                  {/* Overlay Icon */}
                  {' '}
                  {showIcon && (
                  <div
                    className="pointer-events-none absolute left-1/2 top-1/2 flex size-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/60"
                  >
                    {' '}
                    {isPlaying ? (<PlayIcon className="size-10 animate-fade text-white opacity-90" />) : (
                      <PauseIcon className="size-10 animate-fade text-white opacity-90" />)}
                    {' '}
                  </div>
                  )}
                  {' '}
                  {' '}
                  <div className="absolute inset-x-0 bottom-0 z-1 h-[200px] bg-story-bottom " />

                  {/* Interaction buttons */}
                  <div className="absolute right-4 top-8 z-2 flex flex-col items-center gap-5">
                    <StoriesActionIcons
                      onClick={onClose}
                      icon={<XMarkIcon className="size-7" />}
                    />
                    <StoriesActionIcons
                      onClick={() => toggleMuteAll('prev')}
                      icon={
                    muted ? (
                      <SpeakerXMarkIcon className="size-5" />
                    ) : (
                      <SpeakerWaveIcon className="size-5" />
                    )
                  }
                    />
                    {/* Contact */}
                    {' '}
                    {isAuth ? (
                      <Button type="button" color="secondary" rounding="full" size="sm" className="!size-10 !p-0" onClick={() => { const stored = sessionStorage.getItem('global.cv.detail.unblock'); if (stored === 'true') { void triggerUnlock({ jobId: 0, jobSlug: '', title: '' }); } else { setConfirmModal(true); } }}>
                        {' '}
                        <PhoneIcon className="size-5" />
                        {' '}
                      </Button>
                    ) : (
                      <LinkStyled href="/login" color="secondary" rounding="full" size="sm" className="!size-10 !p-0">
                        {' '}
                        <PhoneIcon className="size-5" />
                        {' '}
                      </LinkStyled>
                    )}
                  </div>

                  {/* Progress bar */}
                  <div
                    role="presentation"
                    className="absolute left-0 top-0 z-1 h-1 w-full cursor-pointer bg-gray-400"
                    onClick={(e) => {
                      const activeVideo = videoRefs.current[swiperIndex];
                      if (!activeVideo) return;

                      const rect = (e.target as HTMLElement).getBoundingClientRect();
                      const clickX = e.clientX - rect.left;
                      const newTime = (clickX / rect.width) * activeVideo.duration;
                      activeVideo.currentTime = newTime;
                    }}
                  >
                    <div
                      ref={(el) => {
                        if (el) progressRefs.current[index] = el;
                      }}
                      className="h-1 bg-secondary transition-all duration-200 ease-linear"
                    />
                  </div>

                  {/* Video */}
                  <video
                    ref={(el) => {
                      if (el) videoRefs.current[index] = el;
                    }}
                    src={Math.abs(index - swiperIndex) <= 1 ? streamUrl : undefined}
                    poster={item.thumbnailUrl ?? ''}
                    className="size-full bg-[#000000] object-cover md:object-contain"
                    loop
                    onClick={() => handleVideoClick(index)}
                    muted={muted}
                    onTimeUpdate={(e) => {
                      const video = e.currentTarget;
                      const progressEl = progressRefs.current[index];
                      if (!progressEl) return;
                      progressEl.style.width = `${
                        (video.currentTime / video.duration) * 100
                      }%`;
                    }}
                    playsInline
                    controls={false}
                  />

                  {/* First click overlay (play) */}
                  {!hasInteracted && (
                  <div
                    role="presentation"
                    onClick={() => handleStoriesStart(index)}
                    className="absolute inset-0 z-1 flex flex-col items-center justify-center bg-dark/30"
                  >
                    <div className="flex size-16 items-center justify-center rounded-full bg-black/60">
                      <PlayIcon className="size-10 text-white opacity-90" />
                    </div>
                  </div>
                  )}

                  {/* Overlay question text */}
                  <div
                    className="absolute inset-x-0 bottom-0 z-1 flex flex-col items-center justify-between px-3 py-5 text-white md:bottom-0 md:flex-row"
                  >
                    <div className="mb-4 text-center text-md font-bold md:text-left">
                      {item.question}
                    </div>
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>


          {/* Custom navigation buttons */}
          <div className="absolute right-4 top-1/2 z-2 -mt-12  flex flex-col gap-4">
            <button type="button" className="prev-btn rounded-full bg-surface p-2 text-ink">
              <ArrowUpIcon className="size-5" />
            </button>
            <button type="button" className="next-btn rounded-full bg-surface p-2 text-ink">
              <ArrowDownIcon className="size-5" />
            </button>
          </div>
        </>
      )}
    </div>
  );
};
